import produce from "immer";
import create from "zustand";

import { ArtistId } from "../../models/Artist";
import { AssignmentId } from "../../models/Assignment";
import { CommentsDoc } from "../../models/Comment";
import { GroupId } from "../../models/Group";
import {
  Song,
  SongId,
  SongRef,
  FreshSong,
  SongsFilterParams,
  MediaReference,
} from "../../models/Song";
import { Tag } from "../../models/Tag";
import { EndOfTime, SpecifiedDate, Timestamp } from "../../models/Timeline";

import { ArtistService } from "../../services/ArtistService";
import { SongService } from "../../services/SongService";
import { CommentThreadService } from "../../services/CommentThreadService";
import { useTransmission } from "./useTransmission";
import {
  compareTimestamps,
  shuffle,
  filterSongsByParams,
} from "../../util/sugar";
import isEmpty from "lodash.isempty";
import { RegionId } from "../../models/Region";
import { ENABLE_GROUPS } from "../../config/env";

export type songHookStatus = "fetching" | "idle" | "initial" | "deleting";

export type SongDirectory = {
  assignments: Record<AssignmentId, SongRef[]>;
  timeline: SongRef[];
  sortedSongs: SongRef[];
  tagged?: SongRef[];
  archive?: SongRef[];
  artists: Record<ArtistId, SongRef[]>;
  regionSongs: Record<RegionId, SongRef[]>;
  artistNumberSongs: Record<ArtistId, number>;
  regionNumnerSongs: Record<RegionId, number>;
  cursor: Timestamp;
  pageSize: number;
  songsTotal: number;
  songs: Record<SongId, Song>;
  status: songHookStatus;
  initialFetchStatus: songHookStatus;
  empty: SongId[];
  all: SongId[];
  revisions: SongRef[];
  /** Visibility scope (auth state + group set + tiers) the current timeline
   *  was fetched under. The pagination cursor is derived from the timeline
   *  (oldest()), so refs fetched under DIFFERENT scopes must never share it —
   *  fetchSome resets the timeline whenever this key changes, keeping the
   *  endBefore/startAfter window gap-free. The `songs` object cache is
   *  deliberately kept across scope changes so the now-playing pointer and
   *  play queue keep resolving. */
  feedScope?: string;
  /** True when the cached timeline belongs to a different visibility scope
   *  than a feed over `groups` would use — pages use this to trigger a
   *  refetch after login/logout or group-set changes. */
  needsFeedRefresh: (groups: GroupId[]) => boolean;
  observe: (groups: GroupId[]) => void;
  shouldFetch: (id: SongId) => boolean;
  clearSortedSongs: () => void;
  setInitialSortedSongs: () => void;
  retrieve: (id: SongId) => Song | undefined;
  retrieveByArtist: (id: ArtistId) => SongRef[] | undefined;
  clearArtistSongs: (id: ArtistId) => void;
  fetchSongsForArtist: (id: ArtistId) => void;
  fetchSongsForRegion: (region: RegionId, authorIds?: string[]) => void;
  fetchSongsForHashtag: (groups: GroupId[], tag: Tag) => void;
  fetchSongsForDate: (groups: GroupId[], date: string) => void;
  // fetchSongsByParams: (
  //   groups: string[],
  //   assignments: string[],
  //   dateOrder: string,
  //   isShowLowComments: boolean,
  //   specifiedDate: SpecifiedDate,
  //   favorites: SongRef[],
  //   isShowFavorites: boolean,
  //   isRandomSongs: boolean,
  //   randomId: string,
  //   fetchMore?: boolean
  // ) => void;
  songsFilterParams: SongsFilterParams | any;
  fetchSongsForAssignment: (id: string, groupId?: string) => void;
  fetch: (id: SongId, toTimeline?: boolean) => void;
  delete: (id: SongId) => void;
  revise: (id: SongId, description: string, media: MediaReference, title: string, ownerId?: string) => void;
  updateSong: (id: SongId, song: FreshSong) => void;
  fetchSome: (groups: GroupId[], publicOnly?: boolean) => void;
  fetchRevisions: (id: SongId) => void;
  oldest: () => Timestamp;
  oldestFromArtist: (id: ArtistId) => Timestamp;
};

export const useSongs = create<SongDirectory>((set, get) => ({
  assignments: {},
  timeline: [],
  sortedSongs: [],
  archive: [],
  all: [],
  revisions: [],
  artists: {},
  regionSongs: {},
  artistNumberSongs: {},
  regionNumnerSongs: {},
  songsFilterParams: {} as SongsFilterParams,
  cursor: EndOfTime,
  status: "initial",
  initialFetchStatus: "initial",
  pageSize: 10,
  songsTotal: 0,
  songs: {} as Record<SongId, Song>,
  feedScope: undefined,
  needsFeedRefresh: (groups) => {
    const scope = get().feedScope;
    return scope !== undefined && scope !== SongService.feedScopeKey(groups);
  },
  empty: [],
  delete: (id) => {
    set({ status: "deleting" });
    const deletedSong = get().songs[id];
    const originalId = deletedSong?.original;
    const wasRevision = originalId && originalId !== id;

    SongService.delete(id).then(() => {
      const newState = produce(get(), (draft) => {
        draft.timeline = get().timeline.filter((item) => item.id !== id);
        draft.all = get().all.filter((item) => item !== id);
        for (const artistId in draft.artists) {
          draft.artists[artistId] = draft.artists[artistId].filter(
            (songRef) => songRef.id !== id
          );
        }
        draft.revisions = draft.revisions.filter((r) => r.id !== id);
        delete draft.songs[id];
        draft.status = "idle";
      });
      set(newState);

      // If this was the last revision, clear the `original` field from the
      // original song in-memory so its "original" label disappears immediately.
      if (wasRevision && originalId) {
        const remainingRevisions = Object.values(get().songs).filter(
          (s) => s.original === originalId && s.id !== originalId
        );
        if (remainingRevisions.length === 0 && get().songs[originalId]) {
          set(produce(get(), (draft) => {
            delete (draft.songs[originalId] as any).original;
          }));
        }
      }
    });
  },

  observe: (groups) => {
    const onFreshSong = (song: Song) => {
      if (song.updatedAt === null) {
        return;
      }
      const nextState = produce(get(), (draftState) => {
        draftState.songs[song.id] = song;

        if (!draftState.timeline.map((x) => x.id).includes(song.id)) {
          draftState.timeline.unshift(SongService.toRef(song));
        }

        draftState.status = "idle";
        draftState.timeline = draftState.timeline.sort(
          (y, x) => x.updatedAt.seconds - y.updatedAt.seconds
        );
      });
      set(nextState);
    };

    const onRevokeSong = (revokedSongId: string) => {
      const revokedState = produce(get(), (draftState) => {
        delete draftState.songs[revokedSongId];
        draftState.timeline = draftState.timeline.filter(
          (x) => x.id !== revokedSongId
        );
        draftState.all = draftState.all.filter((x) => x !== revokedSongId);
        draftState.status = "idle";
      });
      set(revokedState);
    };

    if (ENABLE_GROUPS) {
      SongService.observe(groups, onFreshSong, onRevokeSong);
    } else {
      SongService.observeAll(onFreshSong, onRevokeSong);
    }
  },

  oldestFromArtist: (id) => {
    const artistSongs = get().artists[id];
    if (artistSongs === undefined || artistSongs.length < 6) {
      return { seconds: -1, nanoseconds: -1 };
    }
    return artistSongs[artistSongs.length - 1].updatedAt;
  },

  revise: async (id, description, media, revisionTitle, callerOwnerId) => {
    const songRepo = get();
    const songs = produce(songRepo.songs, (draftState) => {
      draftState[id].original = id;
    });
    set({ songs, status: "fetching" });
    let revisedSong = get().songs[id];
    const { authors, ownerId: songOwnerId } = revisedSong;
    const ownerId = callerOwnerId || songOwnerId;
    const title = revisionTitle || revisedSong.title;
    const freshRevisedSong = {
      description,
      media,
      title,
      authors,
      ownerId,
    } as FreshSong;
    SongService.revise(id, freshRevisedSong)
      .then((newRevision) => {
        const nextState = produce(get(), (draftState) => {
          draftState.songs[newRevision.id] = newRevision;
          draftState.all.push(newRevision.id);
          // Add to feed immediately (same logic as onFreshSong in observe)
          if (!draftState.timeline.map((x) => x.id).includes(newRevision.id)) {
            draftState.timeline.unshift(SongService.toRef(newRevision));
            draftState.timeline = draftState.timeline.sort(
              (y, x) => x.updatedAt.seconds - y.updatedAt.seconds
            );
          }
          draftState.revisions = [];
          draftState.revisions.push(SongService.toRef(newRevision));
          draftState.revisions.push(SongService.toRef(revisedSong));
          draftState.status = "idle";
        });
        set(nextState);
        useTransmission.setState({ todaysTransmission: newRevision.id });
      })
      .catch(() => {
        set({ status: "idle" });
      });
  },

  updateSong: (id: string, song: FreshSong) => {
    set({ status: "fetching" });

    SongService.updateSong(id, song)
      .then((freshSong) => {
        const nextState = produce(get(), (draftState) => {
          draftState.songs[id] = {
            ...freshSong,
            id,
          };
          draftState.status = "idle";
        });
        set(nextState);
      })
      .catch(() => {
        set({ status: "idle" });
      });
  },

  oldest: () => {
    let timeline = [...get().timeline];
    if (timeline.length === 0) {
      return { seconds: -1, nanoseconds: -1 };
    }
    return timeline.sort((x, y) => x.updatedAt.seconds - y.updatedAt.seconds)[0]
      .updatedAt;
  },

  fetchRevisions: async (songId) => {
    set({ status: "fetching", revisions: [] });
    SongService.fetchRevisions(songId)
      .then((songs) => {
        const revisions = songs.map((x) => SongService.toRef(x));
        const nextState = produce(get(), (draftState) => {
          for (const song of songs.reverse()) {
            draftState.songs[song.id] = song;
          }
          draftState.revisions = revisions;
          draftState.status = "idle";
        });
        set(nextState);
      })
      .catch(() => {
        set({ status: "idle" });
      });
  },

  fetchSome: async (groups, publicOnly = false) => {
    // The cursor below (oldest()) is derived from the timeline, so the
    // timeline must only ever hold refs fetched under ONE visibility scope.
    // When the scope changes (login/logout, different group set) reset the
    // ref list — but keep the `songs` object cache so the currently playing
    // song and the play queue stay resolvable.
    const scope = SongService.feedScopeKey(groups);
    if (get().feedScope !== scope) {
      set({ timeline: [], feedScope: scope });
    }
    const directory = get();
    const oldest = directory.oldest();
    set({ status: "fetching", initialFetchStatus: "idle" });
    try {
      const fetchPromise = ENABLE_GROUPS
        ? SongService.fetchMore(groups, directory.pageSize, oldest)
        : SongService.fetchMoreAll(directory.pageSize, oldest, publicOnly);

      const songBatch = (await fetchPromise.then(async (batch) =>
        Promise.all(batch.map(async (song) => {
          const comments = await Promise.race([
            CommentThreadService.fetch(song.id)
              .then((threadDoc) => (threadDoc as CommentsDoc).comments || [])
              .catch(() => []),
            new Promise<[]>((resolve) => setTimeout(() => resolve([]), 5000)),
          ]);
          return { ...song, comments };
        }))
      )) as Song[];

      const nextState = produce(get(), (draftState) => {
        for (const song of songBatch.reverse()) {
          draftState.songs[song.id] = song;

          if (!draftState.timeline.map((x) => x.id).includes(song.id)) {
            draftState.timeline.push(SongService.toRef(song));
          }

          draftState.timeline = draftState.timeline.sort(
            (y, x) => x.updatedAt.seconds - y.updatedAt.seconds
          );
        }
        draftState.status = "idle";
      });
      set(nextState);
    } catch(e) {
      console.log("error",e);
      set({ status: "idle" });
    }
  },

  fetchSongsForDate: async (groups, date) => {
    set({ status: "fetching" });
    const archiveSongs = ENABLE_GROUPS
      ? await SongService.fetchSongsOnDate(groups, date)
      : await SongService.fetchSongsOnDateAll(date);
    const archive: SongRef[] = archiveSongs.map(({ id, updatedAt }) => ({
      id,
      updatedAt,
    }));
    const newSongs: Record<SongId, Song> = archiveSongs
      .map((song) => ({ [song.id]: song }))
      .reduce((a, b) => {
        return { ...a, ...b };
      }, {});
    set({ archive, songs: { ...get().songs, ...newSongs }, status: "idle" });
  },

  fetchSongsForHashtag: async (groups, tag) => {
    try {
      const tagged = await SongService.fetchSongsWithHashtag(tag, groups);
      const taggedRefs = tagged.map(({ id, updatedAt }) => ({ id, updatedAt }));
      const nextState = produce(get(), (draftState) => {
        for (const song of tagged) {
          draftState.songs[song.id] = song;
        }
        draftState.tagged = taggedRefs;
        draftState.status = "idle";
      });
      set(nextState);
    } catch {
      set({ status: "idle" });
    }
  },

  fetchSongsForArtist: async (id) => {
    const directory = get();
    set({ status: "fetching" });
    try {
      const songs = (await ArtistService.fetchMoreSongs(
        id,
        directory.oldestFromArtist(id),
        directory.pageSize
      ).then(async (batch) => {
        const enrichedBatch: any = [];

        for (const song of batch) {
          await CommentThreadService.fetch(song.id).then((threadDoc) => {
            const comments = (threadDoc as CommentsDoc).comments || [];
            enrichedBatch.push({ ...song, comments });
          });
        }
        return enrichedBatch;
      })) as Song[];

      const songsNumber = await ArtistService.fetchArtistSongsNumber(id);

      const nextState = produce(get(), (draftState) => {
        if (draftState.artists[id] === undefined) {
          draftState.artists[id] = songs.map((x) => SongService.toRef(x));
        } else {
          for (const song of songs) {
            if (!draftState.artists[id].map((x) => x.id).includes(song.id)) {
              draftState.artists[id].push(SongService.toRef(song));
            }
          }
        }
        draftState.artists[id].sort((x, y) =>
          compareTimestamps(y.updatedAt, x.updatedAt)
        );
        for (const song of songs) {
          draftState.songs[song.id] = song;
        }

        draftState.artistNumberSongs[id] = songsNumber;
        draftState.status = "idle";
      });
      set(nextState);
    } catch {
      set({ status: "idle" });
    }
  },

  fetchSongsForAssignment: async (id, groupId) => {
    set({ status: "fetching" });

    const directory = get();
    try {
      const songsForAssignment = (await SongService.fetchSongsForAssignment(
        id,
        groupId
      )) as Song[];

      let songs: any = songsForAssignment;

      // for (const song of songsBatch) {
      //   await CommentThreadService.fetch(song.id).then((threadDoc) => {
      //     songs.push({
      //       ...song,
      //       comments: (threadDoc as CommentsDoc).comments || [],
      //     });
      //   });
      // }

      const nextState = produce(directory, (draftState) => {
        if (draftState.assignments[id] === undefined) {
          draftState.assignments[id] = songs.map((x: any) =>
            SongService.toRef(x)
          );
        } else {
          for (const song of songs) {
            if (!draftState.assignments[id].some((x) => x.id === song.id)) {
              draftState.assignments[id].push(SongService.toRef(song));
            }
          }
        }

        for (const song of songs) {
          draftState.songs[song.id] = song;
        }

        draftState.songsTotal = songsForAssignment.length;
        draftState.status = "idle";
      });
      set(nextState);
    } catch {
      set({ status: "idle" });
    }
  },
  fetchSongsForRegion: async (id, authorIds = []) => {
    if (authorIds.length === 0) return;
    const directory = get();
    set({ status: "fetching" });

    try {
      // Initialize regionSongs if undefined
      if (!directory.regionSongs[id]) {
        const nextState = produce(directory, (draftState) => {
          draftState.regionSongs[id] = [];
          draftState.regionNumnerSongs[id] = 0;
        });
        set(nextState);
      }

      const songs = await SongService.fetchSongsForRegion(
        authorIds,
        directory.pageSize,
        directory.regionNumnerSongs[id]
      ).then(async (batch) => {
        const enrichedBatch: any = [];
        for (const song of batch) {
          await CommentThreadService.fetch(song.id).then((threadDoc) => {
            const comments = (threadDoc as CommentsDoc).comments || [];
            enrichedBatch.push({ ...song, comments });
          });
        }
        return enrichedBatch;
      });
  
      const nextState = produce(get(), (draftState) => {
        // Store songs in regionSongs map
        if (!draftState.regionSongs[id]) {
          draftState.regionSongs[id] = songs;
        } else {
          // Add new songs if they don't exist
          for (const song of songs) {
            if (!draftState.regionSongs[id].some(s => s.id === song.id)) {
              draftState.regionSongs[id].push(song);
            }
          }
        }
  
        // Also store in main songs record
        for (const song of songs) {
          draftState.songs[song.id] = song;
        }
  
        draftState.regionNumnerSongs[id] = (directory.regionNumnerSongs[id] || 0) + songs.length;
        draftState.status = "idle";
      });
      
      set(nextState);
    } catch (error) {
      console.error("Error fetching songs for region:", error);
      set({ status: "idle" });
    }
  },

  // fetchSongsByParams: async (
  //   groups: string[],
  //   assignments: string[],
  //   dateOrder: string,
  //   isShowLowComments: boolean,
  //   specifiedDate: SpecifiedDate,
  //   favorites: SongRef[],
  //   isShowFavorites: boolean,
  //   isRandomSongs: boolean,
  //   randomId: string,
  //   fetchMore: boolean = false
  // ) => {
  //   set({
  //     status: "fetching",
  //     songsFilterParams: {
  //       groups,
  //       assignments,
  //       dateOrder,
  //       isShowLowComments,
  //       specifiedDate,
  //       favorites,
  //       isShowFavorites,
  //       isRandomSongs,
  //       randomId,
  //     },
  //   });

  //   try {
  //     // When sorting by favorites, we immediately have the entire collection of songs.
  //     // Therefore, we sort without the help of the Firebase API by pure function
  //     if (isShowFavorites && !isEmpty(favorites)) {
  //       const nextState = produce(get(), (draftState) => {
  //         for (const song of favorites) {
  //           draftState.songs[song.id] = {
  //             ...get().songs[song.id],
  //             comments: song.comments,
  //           };
  //         }

  //         const allFavoritesSongs = favorites.map(
  //           (item) => get().songs[item.id]
  //         );

  //         draftState.sortedSongs = filterSongsByParams(
  //           allFavoritesSongs,
  //           groups,
  //           assignments,
  //           dateOrder,
  //           isShowLowComments,
  //           specifiedDate,
  //           isRandomSongs
  //         );
  //       });

  //       set(nextState);
  //     } else {
  //       const sortedSongsNumber = get().sortedSongs.length;
  //       const lastVisibleSongId =
  //         sortedSongsNumber === 0
  //           ? ""
  //           : get().sortedSongs[sortedSongsNumber - 1].id;

  //       const songBatch = (await SongService.fetchSongsByParams(
  //         groups,
  //         assignments,
  //         dateOrder,
  //         isShowLowComments,
  //         specifiedDate,
  //         isRandomSongs,
  //         randomId,
  //         fetchMore,
  //         lastVisibleSongId
  //       ).then(async (batch) => {
  //         const enrichedBatch: any = [];

  //         for (const song of batch) {
  //           await CommentThreadService.fetch(song.id).then((threadDoc) => {
  //             const comments = (threadDoc as CommentsDoc).comments || [];
  //             enrichedBatch.push({ ...song, comments });
  //           });
  //         }
  //         return enrichedBatch;
  //       })) as Song[];

  //       const nextState = produce(get(), (draftState) => {
  //         const freshSortedSongs: any = [];

  //         for (const song of songBatch) {
  //           draftState.songs[song.id] = song;
  //           freshSortedSongs.push(SongService.toRef(song));
  //         }

  //         draftState.sortedSongs = fetchMore
  //           ? [...get().sortedSongs, ...freshSortedSongs]
  //           : freshSortedSongs;
  //       });

  //       set(nextState);
  //     }

  //     set({ status: "idle" });
  //   } catch {
  //     set({ status: "idle" });
  //   }
  // },

  clearSortedSongs: () => {
    const nextState = produce(get(), (draftState) => {
      draftState.sortedSongs = [];
    });

    set(nextState);
  },

  setInitialSortedSongs: () => {
    const nextState = produce(get(), (draftState) => {
      draftState.sortedSongs = get().timeline.slice(0, 10);
    });

    set(nextState);
  },

  shouldFetch: (id) => {
    const directory = get();
    return !directory.all.includes(id) && !directory.empty.includes(id);
  },

  retrieve: (id) => {
    const directory = get();
    return directory.songs[id];
  },

  retrieveByArtist: (id) => {
    const directory = get();
    return directory.artists[id];
  },

  // Reset an artist's loaded song list so the feed re-paginates cleanly from
  // the newest song. Without this, songs loaded individually elsewhere (e.g.
  // opening an old song's detail page) get pushed into artists[id]; they then
  // sort to the bottom of the artist feed and, because pagination uses the
  // oldest loaded song as its cursor (oldestFromArtist), "Load More" jumps to
  // the deep past instead of continuing from the newest page.
  clearArtistSongs: (id) => {
    set(
      produce(get(), (draftState) => {
        delete draftState.artists[id];
      })
    );
  },

  fetch: async (id, toTimeline = false) => {
    const directory = get();
    const exists = directory.songs[id];

    if (exists !== undefined) return;
    set({ status: "fetching" });

    try {
      const song = await SongService.fetch(id);
      // Add Fetched Entries to directory
      // and push to empty state if fetched song was already deleted by author
      const nextState = produce(get(), (draftState) => {
        if (song.id) {
          song.authors.forEach((item) => {
            if (
              draftState.artists[item] &&
              !get().artists[item].some((item) => item.id === song.id)
            ) {
              draftState.artists[item].push(song);
            } else {
              draftState.artists[item] = [song];
            }
          });

          draftState.songs[song.id] = song;

          draftState.all.push(song.id);
          toTimeline && draftState.timeline.unshift(song);
        } else {
          draftState.empty.push(id);
        }

        draftState.status = "idle";
      });
      set(nextState);
    } catch (e: any) {
      // Permission-denied reads (e.g. a cached/favorited ref whose song went
      // private) are negative-cached like a deleted song so SongItem doesn't
      // refetch in a loop. Transient failures (offline, timeouts) stay
      // retryable — negative-caching those would hide the song all session.
      if (e?.code === "permission-denied" || e?.code === "not-found") {
        const nextState = produce(get(), (draftState) => {
          if (!draftState.empty.includes(id)) draftState.empty.push(id);
          draftState.status = "idle";
        });
        set(nextState);
      } else {
        set({ status: "idle" });
      }
    }
  },
}));
